import { Icon, IconName } from '@usealloy/component-library';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';

import { UserContext } from '~/dashboard/context';
import { routes } from '~/helpers/routes';

import { Sidebar } from '../component/Sidebar';
import {
  isJourneyActive,
  isPrefixActive,
  isWorkflowsActive,
} from './routeMatchers';

/**
 * "Build" nav group — Journeys, Workflows, AI Assistant, Partner Center.
 *
 * Mirrors the FE2.0 SideNav `NavList.build` structure (Partner Center occupies the
 * Marketplace slot); items, conditions, and links are ported verbatim from the legacy
 * sidebar (`~/dashboard/components/Sidebar/Sidebar.jsx`), except AI Assistant, which
 * moved here from the Explore group to sit between Workflows and Marketplace.
 */
export const BuildGroup = () => {
  const { featureFlags, permissions } = useContext(UserContext);
  const { pathname } = useLocation();

  const showJourneysLink = featureFlags?.journeys && permissions?.['READ:Journey:journey_id'];
  const showWorkflowsLink = permissions?.['READ:Applications:application_id']
    || permissions?.['READ:Journey:journey_id'];
  const showAiOverviewLink = featureFlags?.enableAiOverview;
  const showPartnerCenterLink = featureFlags?.enablePartnerCenterV2 && permissions?.['READ:Services:null'];

  if (!showJourneysLink && !showWorkflowsLink && !showAiOverviewLink
    && !showPartnerCenterLink) return null;

  return (
    <Sidebar.Group>
      <Sidebar.GroupLabel>
        <span className="fs-unmask">Build</span>
      </Sidebar.GroupLabel>
      <Sidebar.Menu>
        {showJourneysLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-journeys"
              href={`${window.location.origin}${routes.v3.dashboard.journeys.list}`}
              isActive={isJourneyActive(
                routes.v3.dashboard.journeys.list,
                pathname,
              )}
              tooltip="Journeys"
            >
              <Icon icon={IconName.MapJourney} size="large" />
              <span className="fs-unmask">Journeys</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showWorkflowsLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-workflows"
              href={`${window.location.origin}${routes.v3.dashboard.workflows.list}`}
              isActive={isWorkflowsActive(pathname)}
              tooltip="Workflows"
            >
              <Icon icon={IconName.Workflows} size="large" />
              <span className="fs-unmask">Workflows</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showAiOverviewLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-ai-overview"
              href={`${window.location.origin}${routes.v3.dashboard.aiOverview.list}`}
              isActive={isPrefixActive(
                routes.v3.dashboard.aiOverview.list,
                pathname,
              )}
              tooltip="AI Assistant"
            >
              <Icon icon={IconName.Sparkles} size="large" />
              <span className="fs-unmask">AI Assistant</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}

        {showPartnerCenterLink && (
          <Sidebar.MenuItem>
            <Sidebar.MenuButton
              as="a"
              data-dd-action-name="alloydd-sidebar-nav-partner-solutions"
              href={`${window.location.origin}${routes.v3.dashboard.partnerSolutions.list}`}
              isActive={isPrefixActive(
                routes.v3.dashboard.partnerSolutions.list,
                pathname,
              )}
              tooltip="Marketplace"
            >
              <Icon icon={IconName.PartnerSolutions} size="large" />
              <span className="fs-unmask">Marketplace</span>
            </Sidebar.MenuButton>
          </Sidebar.MenuItem>
        )}
      </Sidebar.Menu>
    </Sidebar.Group>
  );
};
